import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InvoiceBatchClient, InvoiceBatchDTO, InvoiceBatchRegenerationStatusDTO, ApiResponse_string, ApiResponse_iEnumerable_1 } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { parseUtc } from '../utils/dateUtils/parseUtc';

const ACTIVE_POLL_MS = 2000;
// A chron-scheduled run can sit in Queued for a while - no need to poll that fast
const SCHEDULED_POLL_MS = 15000;

/**
 * Latest regeneration run for a batch. Polls only while a run is queued or running,
 * and stops by itself once it completes, fails or stalls.
 */
export const useInvoiceBatchRegenerationStatus = (batchId?: string) => {
    return useQuery({
        queryKey: ['invoiceBatches', batchId?.toLowerCase(), 'regenerationStatus'],
        queryFn: async (): Promise<InvoiceBatchRegenerationStatusDTO | null> => {
            const apiClient = new InvoiceBatchClient(API_BASE_URL, axiosInstance);
            const response = await apiClient.getInvoiceBatchRegenerationStatus(batchId!);
            return response?.data ?? null;
        },
        enabled: !!batchId,
        refetchInterval: (query) => {
            const status = query.state.data;
            if (!status?.isActive) return false;

            const requested = parseUtc(status.requestedUtc);
            const startsInMs = requested ? requested.getTime() - Date.now() : 0;
            return status.status === 'Queued' && startsInMs > 60000 ? SCHEDULED_POLL_MS : ACTIVE_POLL_MS;
        },
    });
};

export const useInvoiceBatchApi = () => {
    const apiClient = new InvoiceBatchClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: InvoiceBatchDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createInvoiceBatch(data);
        } catch (err: any) {
            setError(err.message || 'Error creating InvoiceBatch');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: InvoiceBatchDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateInvoiceBatch(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating InvoiceBatch');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getInvoiceBatchById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching InvoiceBatch');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchInvoiceBatches(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching InvoiceBatches');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteInvoiceBatch(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting InvoiceBatch');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportInvoiceBatches(filters);
        } catch (err: unknown) {
            //handleHookError(err, 'Error exporting InvoiceBatches', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const regenerate = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.regenerateInvoiceBatch(id);
        } catch (err: any) {
            setError(err.message || 'Error regenerating InvoiceBatch');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getMemberBatches = async (memberId: string): Promise<ApiResponse_iEnumerable_1> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberInvoiceBatches(memberId);
            return response;
        } catch (err: any) {
            setError(err.message || 'Error fetching member invoice batches');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const sendRenewalNotices = async (id: string): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.sendBatchRenewalNotices(id);
        } catch (err: any) {
            setError(err.message || 'Error sending renewal notices');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, extract, remove, regenerate, getMemberBatches, sendRenewalNotices, isLoading, error };
};